import type {
  CursorPaginatedResponse,
  Vehicle,
  VehicleCustomerList,
  VehicleListParams,
} from '@/libs/interfaces'

import { MOCK_ORG_ID, mockCustomers, mockVehicles } from '../fixtures/seed'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const vehicleStore = createEntityStore<Vehicle, 'driverCarID'>(mockVehicles, 'driverCarID')

const toCustomerList = (vehicle: Vehicle): VehicleCustomerList => ({
  driverCar: vehicle,
  driver: mockCustomers.find((customer) => customer.driverID === vehicle.driverID),
})

const filterVehicles = (params: VehicleListParams): Vehicle[] =>
  vehicleStore
    .getAll()
    .filter((vehicle) =>
      matchesSearch(vehicle, params.search, [
        'driverCarRegistrationNumber',
        'driverCarBrand',
        'driverCarModel',
      ]),
    )

export const vehiclesApiMock = {
  create: async (payload: Partial<Vehicle>): Promise<Vehicle> => {
    const vehicle: Vehicle = {
      driverCarID: genID('vehicle'),
      driverCarRegistrationNumber: '',
      orgID: MOCK_ORG_ID,
      driverCarStatus: 'Active',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...payload,
    }
    vehicleStore.insert(vehicle)
    return delay(vehicle)
  },
  delete: async (payload: Partial<Vehicle>): Promise<Vehicle> => {
    const vehicle = vehicleStore.getById(payload.driverCarID ?? '')
    if (payload.driverCarID) vehicleStore.remove(payload.driverCarID)
    return delay(vehicle ?? {})
  },
  getByCustomer: async (_orgID: string, driverID: string): Promise<Vehicle[]> =>
    delay(vehicleStore.find((vehicle) => vehicle.driverID === driverID)),
  getByID: async (_orgID: string = '', driverCarID: string = ''): Promise<Vehicle> =>
    delay(vehicleStore.getById(driverCarID) ?? {}),
  getByRegNumber: async (
    _orgID: string = '',
    driverCarRegistrationNumber: string = '',
  ): Promise<Vehicle> =>
    delay(
      vehicleStore.find(
        (vehicle) => vehicle.driverCarRegistrationNumber === driverCarRegistrationNumber,
      )[0] ?? {},
    ),
  list: async (params?: VehicleListParams): Promise<VehicleCustomerList[]> =>
    delay(filterVehicles(params ?? ({} as VehicleListParams)).map(toCustomerList)),
  listCursor: async (
    params: VehicleListParams,
  ): Promise<CursorPaginatedResponse<VehicleCustomerList>> =>
    delay(paginate(filterVehicles(params).map(toCustomerList), params)),
  update: async (payload: Partial<Vehicle>) => {
    const updated = payload.driverCarID
      ? vehicleStore.update(payload.driverCarID, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? payload)
  },
}
